import React, { useState } from 'react';
import { Trash2, Copy, Check, File as FileIcon, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Grid card for a single MediaAsset. Pure presentational — all interactions
 * are bubbled via callbacks so the parent (MediaLibrary / MediaPicker) can
 * decide whether a click means "open" vs "pick".
 */
function prettySize(bytes) {
  if (!bytes) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

export default function MediaAssetCard({
  asset,
  selected = false,
  onSelect,
  onDelete,
  onCopyUrl,
}) {
  const [copied, setCopied] = useState(false);
  const isImage = (asset.mimeType || '').startsWith('image/');
  const isPending = asset.status === 'pending_upload';
  const isFailed = asset.status === 'failed';

  const handleCopy = (e) => {
    e.stopPropagation();
    const url = asset.publicUrl || '';
    if (!url) return toast.error('لا يوجد رابط');
    navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    if (onCopyUrl) onCopyUrl(asset);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (!onDelete) return;
    if (window.confirm('هل تريد حذف هذه الصورة نهائياً؟')) onDelete(asset);
  };

  return (
    <button
      type="button"
      onClick={() => onSelect && onSelect(asset)}
      className={[
        'group relative flex flex-col rounded-2xl border bg-white overflow-hidden text-right transition-all',
        selected
          ? 'border-blue-500 ring-2 ring-blue-200 shadow-md'
          : 'border-gray-100 hover:border-blue-200 hover:shadow-md',
      ].join(' ')}
    >
      <div className="aspect-square bg-gray-50 relative overflow-hidden flex items-center justify-center">
        {isImage && asset.publicUrl ? (
          <img
            src={asset.publicUrl}
            alt={asset.altText || asset.originalName || 'media'}
            loading="lazy"
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-gray-400">
            <FileIcon className="w-10 h-10" />
            <span className="text-[10px] uppercase tracking-wide">{(asset.mimeType || '').split('/')[1] || 'file'}</span>
          </div>
        )}

        {/* Status overlay for non-ready assets */}
        {(isPending || isFailed) && (
          <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-1 text-white text-xs">
            {isFailed ? <AlertCircle className="w-6 h-6" /> : null}
            <span>{isPending ? 'قيد الرفع...' : 'فشل الرفع'}</span>
          </div>
        )}
      </div>

      <div className="p-2.5 border-t border-gray-100">
        <div className="text-xs font-medium text-gray-700 truncate">{asset.originalName || 'بدون اسم'}</div>
        <div className="flex items-center justify-between mt-1 text-[11px] text-gray-400">
          <span>{prettySize(asset.sizeBytes)}</span>
          <span>{asset.folder}</span>
        </div>
      </div>

      {/* Hover actions */}
      <div className="absolute top-2 left-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={handleCopy}
          className="p-1.5 rounded-lg bg-white/90 backdrop-blur shadow text-gray-700 hover:text-blue-600"
          title="نسخ الرابط"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
        {onDelete && (
          <button
            onClick={handleDelete}
            className="p-1.5 rounded-lg bg-white/90 backdrop-blur shadow text-gray-700 hover:text-rose-600"
            title="حذف"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </button>
  );
}
